import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Popover } from "@shared/v2/Popover";
import Avatar from "@shared/v2/Avatar";
import { updatePrimaryAgentOrPond } from "../serviceHelpers";
import { useGetAgentState } from "../agentOrPondHookHelpers";
import "../../Filters/components/PondOrAgentFilter/styles.scss";
import { updatePrimaryAgent } from "../../PersonDetail/actions/personDetailActionCreators";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";

const AgentOrPondSelect = ({ personId, primaryAgent, leadPond, small = false, stacked = false }) => {
  const featureFlags = useFeatureFlags();
  const [state, setState, handleChange] = useGetAgentState(featureFlags.leadPond);
  const [primaryAgentModel, setPrimaryAgent] = useState(primaryAgent);
  const [leadPondModel, setLeadPond] = useState(leadPond);
  const dispatch = useDispatch();

  const onChange = (params) => {
    const oldParams = {
      primary_agent: primaryAgentModel,
      lead_pond: leadPondModel,
    };
    updatePrimaryAgentOrPond({
      data: params,
      errorCb: () => {
        setPrimaryAgent(oldParams.primary_agent);
        setLeadPond(oldParams.lead_pond);
      },
      successCb: (response) => {
        dispatch(updatePrimaryAgent(response.data.primary_agent_uuid));
      },
    });
  };

  const handleAgentSelect = (agent) => {
    const params = {
      primary_agent: agent.id.toString(),
      lead_pond: null,
      people_ids: [personId.toString()],
    };
    onChange(params);
    setLeadPond(null);
    setPrimaryAgent(agent);
  };

  const handlePondSelect = (pond) => {
    const params = {
      primary_agent: null,
      lead_pond: pond.id,
      people_ids: [personId.toString()],
    };
    setLeadPond(pond);
    setPrimaryAgent(null);
    onChange(params);
  };

  const { filter, filteredAssignableAgents, filteredAssignablePonds } = state;

  return (
    <Popover placement="bottom" maxHeight={400} forceTriggerWidth>
      <Popover.Trigger>
        {(leadPondModel || primaryAgentModel) && (
          <button
            className={`tw-bg-transparent tw-border-0 tw-flex tw-items-center tw-gap-[8px] tw-w-full tw-overflow-hidden tw-p-0 ${stacked ? " tw-mb-[10px]" : ""}`}
            type="button"
            onClick={() => setState({ ...state, show: true })}
          >
            <Avatar
              src={leadPondModel ? null : primaryAgentModel.image_src}
              alt={leadPondModel?.name || primaryAgentModel?.name}
              size={small ? "small" : "medium"}
            />
            <div className="tw-flex-1 tw-overflow-hidden tw-text-left">
              <div className="tw-text-12d tw-font-semibold tw-truncate">
                {leadPondModel?.name || primaryAgentModel?.name}
              </div>
              {!small && (
                <small className="tw-font-bold tw-text-11d">
                  {!leadPondModel ? "Primary Agent" : "Lead Pond"}
                </small>
              )}
            </div>
            <span
              className={`tw-ml-4 tw-flex tw-items-center tw-justify-center ${
                small ? "" : "tw-bg-gray-10 tw-h-[22px] tw-w-[22px] tw-rounded-full"
              }`}
            >
              <i className="fa fa-caret-down tw-text-[14px] tw-text-gray-50" />
            </span>
          </button>
        )}
      </Popover.Trigger>

      <Popover.Content className="tw-max-h-[400px]">
        <div className="tw-p-4">
          <input
            className="tw-w-full tw-border tw-border-gray-10"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={handleChange}
            placeholder="Search..."
            value={filter}
          />
        </div>

        {filteredAssignablePonds?.length > 0 && (
          <p className="tw-text-[12px] tw-py-1 tw-px-2 tw-bg-neutral-gray-10">Pond</p>
        )}
        {filteredAssignablePonds?.map((pond) => (
          <Popover.Item key={pond.id} onClick={() => handlePondSelect(pond)}>
            <div className="tw-overflow-hidden tw-flex tw-items-center tw-gap-[8px]" title={pond.name}>
              <Avatar src={pond.image_src} alt={pond.name} />
              <div className="tw-flex-1 tw-whitespace-nowrap [text-overflow:ellipsis] tw-overflow-hidden">
                {pond.name}
              </div>
            </div>
          </Popover.Item>
        ))}

        {filteredAssignableAgents?.length > 0 && (
          <p className="tw-text-[12px] tw-py-1 tw-px-2 tw-bg-neutral-gray-10">Team</p>
        )}
        {filteredAssignableAgents?.map((agent) => (
          <Popover.Item key={agent.id} onClick={() => handleAgentSelect(agent)}>
            <div className="tw-overflow-hidden tw-flex tw-items-center tw-gap-[8px]" title={agent.name}>
              <Avatar src={agent.image_src} alt={agent.name} />
              <div className="tw-flex-1 tw-whitespace-nowrap [text-overflow:ellipsis] tw-overflow-hidden">
                {agent.name}
              </div>
            </div>
          </Popover.Item>
        ))}
      </Popover.Content>
    </Popover>
  );
};

AgentOrPondSelect.propTypes = {
  personId: PropTypes.number.isRequired,
  primaryAgent: PropTypes.shape(),
  leadPond: PropTypes.shape(),
  small: PropTypes.bool,
  stacked: PropTypes.bool,
};
AgentOrPondSelect.defaultProps = {
  primaryAgent: null,
  leadPond: null,
  small: false,
  stacked: false,
};

export default AgentOrPondSelect;
